import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["line", "template"]
  static values = { ledgerId: Number, externalTransactionId: Number }

  connect() {
    console.debug("SplitSuggestionsController connected")
  }

  // -----------------------------
  // ACTION: user clicks Suggest
  // -----------------------------
  async suggest(event) {
    const button = event.currentTarget

    button.disabled = true

    try {
      const line = this._lineFromEvent(event)
      if (!line) return

      const snapshot = this.buildSnapshot()

      const triggeringLineId = line.dataset.splitSuggestionsLineId

      const response = await this.fetchSuggestions({
        ledger_id: this.ledgerIdValue,
        external_transaction_id: this.externalTransactionIdValue,
        triggering_line_id: triggeringLineId,
        snapshot: snapshot
      })

      this.renderSuggestions(line, response.suggestions)
    } finally {
      button.disabled = false
    }
  }

  // -----------------------------
  // SNAPSHOT BUILDING
  // -----------------------------
  buildSnapshot() {
    return {
      lines: this.lineTargets.map(line => ({
        id: line.dataset.splitSuggestionsLineId,
        account: this.fieldValue(line, "account"),
        cost_centre: this.fieldValue(line, "cost_centre"),
        amount: this.fieldValue(line, "amount")
      }))
    }
  }

  fieldValue(line, fieldName) {
    const el = line.querySelector(
      `[data-split-suggestions-field="${fieldName}"]`
    )

    return el?.value?.trim() || null
  }

  // -----------------------------
  // API CALL
  // -----------------------------
  async fetchSuggestions(payload) {
    console.debug("Fetching suggestions with payload", payload)
    const response = await fetch("/split_suggestions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name=csrf-token]").content
      },
      body: JSON.stringify(payload)
    })

    if (!response.ok) {
      console.error("Suggestion request failed")
      return { suggestions: [] }
    }

    return await response.json()
  }

  // -----------------------------
  // RENDERING (placeholder v1)
  // -----------------------------
  renderSuggestions(line, suggestions) {
    console.debug(
      "Suggestions for line",
      line.dataset.splitSuggestionsLineId,
      suggestions
    )

    if (!suggestions?.lines?.length) {
      this.flashRow(line)
      return
    }

    const container = this._suggestionContainer(line)
    if (!container) {
      return
    }

    // IMPORTANT: clear existing suggestions first
    container.innerHTML = ""

    suggestions.lines.forEach(suggestion => {
      container.appendChild(
        this._buildSuggestionElement(suggestion)
      )
    })
  }

  flashRow(line) {
    line.classList.add("suggestion-no-results")

    setTimeout(() => {
      line.classList.remove("suggestion-no-results")
    }, 1000)
  }

  // -----------------------------
  // HELPERS
  // -----------------------------
  _lineFromEvent(event) {
    return event.target.closest(
      "[data-split-suggestions-target='line']"
    )
  }

  _suggestionContainer(line) {
    let container = line.querySelector(".suggestions-container")

    // if (!container) {
    //   container = document.createElement("div")
    //   container.className = "suggestions-container"
    //   line.appendChild(container)
    // }

    return container
  }

  _buildSuggestionElement(suggestion) {
    const el = document.createElement("div")

    el.className = "alert alert-primary w-100 py-2 px-2"

    el.innerHTML = `
      <div class="d-flex gap-2 w-100">

        <!-- ICON -->
        <div class="text-primary fs-3 me-1 flex-shrink-0">
          <i class="fa-regular fa-lightbulb"></i>
        </div>

        <!-- MAIN CONTENT -->
        <div class="flex-grow-1 min-w-0">
          <div class="small text-muted">
            ${suggestion.metadata?.reason || ""}
          </div>
          <div class="fw-bold">${this._formatAmount(suggestion.amount)}</div>
        </div>

        <!-- ACTIONS (BOTTOM ALIGNED) -->
        <div class="d-flex flex-column flex-shrink-0">
          <div class="d-flex gap-2 mt-auto">
            <button class="btn btn-sm btn-primary js-suggestion-apply">
              Add
            </button>

            <button class="btn btn-sm btn-outline-secondary js-suggestion-ignore">
              Ignore
            </button>
          </div>
        </div>

      </div>
    `

    const addBtn = el.querySelector(".js-suggestion-apply")
    const ignoreBtn = el.querySelector(".js-suggestion-ignore")

    addBtn.addEventListener("click", () => {
      this._applySuggestion(suggestion)
      el.remove()
    })

    ignoreBtn.addEventListener("click", () => {
      el.remove()
    })

    return el
  }

  _applySuggestion(suggestion) {
    const sourceLine = this.lineTargets.find(line =>
      line.dataset.splitSuggestionsLineId === suggestion.source_line_id
    )

    if (!sourceLine) return

    const template = this.templateTarget.innerHTML
    const uniqueId = Date.now()

    const newLineHtml = template
      .replace(/new_RECORD/g, `new_${uniqueId}`)

    // Insert AFTER the triggering line
    sourceLine.insertAdjacentHTML("afterend", newLineHtml)

    const insertedLine = sourceLine.nextElementSibling

    setTimeout(() => {
      this._populateInsertedLine(insertedLine, suggestion)
      this.dispatch("line-added", { detail: { line: insertedLine } })
    }, 0)

    this.flashInsertedLine(insertedLine)
  }

  _populateInsertedLine(line, suggestion) {
    this._setSelectValue(line, "account", suggestion.account_id)
    this._setSelectValue(line, "cost_centre", suggestion.cost_centre_id)

    const amount = line.querySelector('[data-split-suggestions-field="amount"]')
    if (amount && suggestion.amount != null) {
      amount.value = suggestion.amount
    }
  }

  _setSelectValue(line, fieldName, value) {
    if (!value) return

    const el = line.querySelector(
      `[data-split-suggestions-field="${fieldName}"]`
    )

    if (!el) return

    // only applies to <select>
    if (el.tagName === "SELECT") {
      el.value = value

      // important: trigger change so Stimulus/other logic reacts
      el.dispatchEvent(new Event("change", { bubbles: true }))
    }
  }

  flashInsertedLine(line) {
    line.classList.add("suggestion-inserted")

    setTimeout(() => {
      line.classList.remove("suggestion-inserted")
    }, 1200)
  }

  _formatAmount(amount) {
    if (!amount && amount !== 0) return ""

    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP"
    }).format(amount)
  }
}
